import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Eye, EyeOff, Lock, Mail, User } from "lucide-react";
import {
  validatePassword,
  getPasswordStrengthColor,
  getPasswordStrengthText,
} from "@/lib/passwordUtils";
import fissionLogo from "@/public/fission_logo_1.png";

export function Login() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [userValues, setUserValues] = useState<Record<string, string>>({});
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<{
    isValid: boolean;
    errors: string[];
    strength: "weak" | "medium" | "strong";
  } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetCode, setShowResetCode] = useState(false);
  const [resetCode, setResetCode] = useState("");

  const {
    signIn,
    completeNewPassword,
    signUp,
    confirmSignUp,
    resendSignUp,
    resetPassword,
    confirmResetPassword,
    isAuthenticated,
  } =
    useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const cleanErrorMessage = (msg?: string) =>
    msg?.replace(/^PreSignUp failed with error\s*/i, "") || msg;

  const handlePasswordChange = (password: string) => {
    setUserValues({ ...userValues, password });
    if ((isSignUp || showNewPassword) && password) {
      const validation = validatePassword(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    // Validate for sign up
    if (isSignUp) {
      if (!userValues.firstName?.trim() || !userValues.lastName?.trim()) {
        setError("Please enter your first and last name.");
        setLoading(false);
        return;
      }

      if (userValues.password !== userValues.confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      const validation = validatePassword(userValues.password);
      if (!validation.isValid) {
        setError(validation.errors.join(", "));
        setLoading(false);
        return;
      }
    }

    try {
      if (isSignUp) {
        await signUp(
          userValues.email,
          userValues.password,
          userValues.firstName.trim(),
          userValues.lastName.trim()
        );
        // Account needs email verification
        setPendingEmail(userValues.email);
        setShowOTP(true);
        setSuccess(
          "Account created! Please check your email for the verification code."
        );
      } else {
        await signIn(userValues.email, userValues.password);
        setUserValues({});
        setShowPassword(false);
        setShowConfirmPassword(false);
        navigate("/dashboard");
      }
    } catch (err: any) {
      // Handle specific Cognito errors
      if (err.name === "UserNotConfirmedException") {
        setPendingEmail(userValues.email);
        setShowOTP(true);
        setShowNewPassword(false);
        setShowForgotPassword(false);
        setError(
          "Please verify your email address. Check your inbox for a confirmation code."
        );
      } else if (err.name === "NEW_PASSWORD_REQUIRED") {
        setShowOTP(false);
        setShowForgotPassword(false);
        setShowNewPassword(true);
        setSuccess(null);
        setError(null);
      } else if (err.name === "NotAuthorizedException") {
        setError("Incorrect email or password.");
      } else if (err.name === "UserNotFoundException") {
        setError("User not found. Please sign up first.");
        setIsSignUp(true);
        setShowNewPassword(false);
        setShowForgotPassword(false);
      } else if (err.name === "UsernameExistsException") {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
        setIsSignUp(false);
        setShowNewPassword(false);
        setShowForgotPassword(false);
      } else {
       
        setError(
          cleanErrorMessage(err?.message) ||
            `${isSignUp ? "Sign up" : "Sign in"} failed. Please try again.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await confirmSignUp(pendingEmail || userValues.email, otpCode);
      setSuccess("Email verified successfully! You can now sign in.");
      setShowOTP(false);
      setIsSignUp(false);
      setPendingEmail("");
      setOtpCode("");
      setUserValues({});
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      setError(
        cleanErrorMessage(err?.message) ||
          "Verification failed. Please check your code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError(null);
    setLoading(true);

    try {
      await resendSignUp(pendingEmail || userValues.email);
      setSuccess("Verification code sent! Please check your email.");
    } catch (err: any) {
      setError(cleanErrorMessage(err?.message) || "Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignUp = () => {
    setShowOTP(false);
    setPendingEmail("");
    setOtpCode("");
    setError(null);
    setSuccess(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await resetPassword(userValues.email);
      setShowResetCode(true);
      setSuccess("Password reset code sent to your email.");
    } catch (err: any) {
      setError(cleanErrorMessage(err?.message) || "Failed to send reset code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (userValues.password !== userValues.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    const validation = validatePassword(userValues.password || "");
    if (!validation.isValid) {
      setError(validation.errors.join(", "));
      setLoading(false);
      return;
    }

    try {
      await confirmResetPassword(userValues.email, resetCode, userValues.password);
      setSuccess("Password reset successfully! You can now sign in.");
      setShowForgotPassword(false);
      setShowResetCode(false);
      setResetCode("");
      setUserValues({});
      setPasswordValidation(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
    } catch (err: any) {
      setError(
        cleanErrorMessage(err?.message) || "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (userValues.password !== userValues.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    const validation = validatePassword(userValues.password || "");
    if (!validation.isValid) {
      setError(validation.errors.join(", "));
      setLoading(false);
      return;
    }

    try {
      await completeNewPassword(userValues.password);
      setUserValues({});
      setShowPassword(false);
      setShowConfirmPassword(false);
      setShowNewPassword(false);
      navigate("/dashboard");
    } catch (err: any) {
      setError(
        cleanErrorMessage(err?.message) || "Failed to set new password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-50 via-white to-sales-50">
      <div className="w-full max-w-md space-y-6 rounded-lg border bg-white p-8 shadow-xl">
        {/* Header */}
        <img
          src={fissionLogo}
          alt="Fission AI Portal logo"
          className="h-16 w-16 object-contain mx-auto mb-4"
        />
        <div className="text-center">
          <Link to="/" className="inline-block mb-4">
            <span className="text-2xl font-bold gradient-text">Fission AI Portal</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">
            {showOTP
              ? "Verify Your Email"
              : showNewPassword
              ? "Set New Password"
              : showResetCode
              ? "Reset Password"
              : showForgotPassword
              ? "Reset Password"
              : isSignUp
              ? "Create Account"
              : "Sign In"}
          </h1>
          <p className="text-gray-600 mt-2">
            {showOTP
              ? "Enter the verification code sent to your email"
              : showNewPassword
              ? "Your account needs a new password before you can continue"
              : showResetCode
              ? "Enter the code sent to your email and set a new password"
              : showForgotPassword
              ? "We'll send a password reset code to your email"
              : isSignUp
              ? "Create a new account to get started"
              : "Sign in to access GenAI applications"}
          </p>
        </div>

        {showOTP ? (
          /* OTP Verification Form */
          <form onSubmit={handleOTPSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-red-500/20 p-3 text-sm text-red-700 border border-red-500 flex items-center gap-2"
                style={{ backgroundColor: "#ffe5e5" }} // fallback for red
              >
                <svg
                  className="h-5 w-5 text-red-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01m-6.938 4h13.856c1.054 0 1.658-1.14 1.105-2.03L13.105 4.93a1.25 1.25 0 00-2.21 0l-6.923 13.04c-.553.89.05 2.03 1.105 2.03z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div
                role="status"
                className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400"
              >
                {success}
              </div>
            )}

            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                We sent a verification code to:
              </p>
              <p className="text-sm font-medium text-gray-800">
                {pendingEmail}
              </p>
            </div>

            <div>
              <label htmlFor="otp" className="block text-sm font-medium mb-2">
                Verification Code
              </label>
              <input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full px-3 py-2 border rounded-md bg-background text-center text-lg tracking-widest"
                required
                maxLength={6}
                aria-describedby="otp-hint"
              />
              <p id="otp-hint" className="text-xs text-muted-foreground mt-1">
                Enter the 6-digit code from your email.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || otpCode.length !== 6}
              aria-busy={loading}
              className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Verify Email"}
            </button>

            <div className="flex flex-col gap-2 text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={loading}
                className="text-sm text-primary hover:underline disabled:opacity-50"
              >
                Resend verification code
              </button>
              <button
                type="button"
                onClick={handleBackToSignUp}
                disabled={loading}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                ← Back to sign up
              </button>
            </div>
          </form>
        ) : showNewPassword ? (
          <form onSubmit={handleNewPasswordSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
               className="rounded-md bg-red-500/20 p-3 text-sm text-red-700 border border-red-500 flex items-center gap-2"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                role="status"
                className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400"
              >
                {success}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={userValues.password || ""}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                    passwordValidation && !passwordValidation.isValid
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Create a new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1 text-xs">
                <p className="text-muted-foreground">
                  Minimum 6 characters, must contain numbers and special characters.
                </p>
                {passwordValidation && (
                  <div className="mt-1 space-y-1">
                    <p className={`font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                      Password Strength: {getPasswordStrengthText(passwordValidation.strength)}
                    </p>
                    {passwordValidation.errors.length > 0 && (
                      <ul className="text-red-500 space-y-0.5">
                        {passwordValidation.errors.map((validationError, index) => (
                          <li key={index}>• {validationError}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={userValues.confirmPassword || ""}
                  onChange={(e) =>
                    setUserValues({ ...userValues, confirmPassword: e.target.value })
                  }
                  className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                    userValues.confirmPassword &&
                    userValues.password !== userValues.confirmPassword
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p
                className={`mt-1 text-xs ${
                  userValues.confirmPassword &&
                  userValues.password !== userValues.confirmPassword
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {userValues.confirmPassword &&
                userValues.password !== userValues.confirmPassword
                  ? "Passwords do not match"
                  : "Must match the new password above."}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Updating..." : "Set New Password"}
            </button>

            <div className="text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => {
                  setShowNewPassword(false);
                  setError(null);
                  setSuccess(null);
                  setUserValues({ ...userValues, password: "", confirmPassword: "" });
                  setPasswordValidation(null);
                }}
                className="text-primary hover:underline"
              >
                Back to sign in
              </button>
            </div>
          </form>
        ) : showForgotPassword && !showResetCode ? (
          <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-red-500/20 p-3 text-sm text-red-700 border border-red-500 flex items-center gap-2"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                role="status"
                className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400"
              >
                {success}
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Enter your email address and we&apos;ll send you a code to reset
              your password.
            </p>

            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={userValues.email || ""}
                  onChange={(e) =>
                    setUserValues({ ...userValues, email: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border rounded-md bg-background"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Sending..." : "Send Reset Code"}
            </button>

            <div className="text-center text-sm text-muted-foreground">
              Remember your password?{" "}
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="text-primary hover:underline"
              >
                Sign in
              </button>
            </div>
          </form>
        ) : showResetCode ? (
          <form onSubmit={handleResetCodeSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-red-500/20 p-3 text-sm text-red-700 border border-red-500 flex items-center gap-2"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                role="status"
                className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400"
              >
                {success}
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Enter the verification code sent to your email and choose a new password.
            </p>

            <div>
              <label htmlFor="resetCode" className="block text-sm font-medium mb-2">
                Verification Code
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="resetCode"
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-md bg-background"
                  placeholder="Enter verification code"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={userValues.password || ""}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                    passwordValidation && !passwordValidation.isValid
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Create a new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-1 text-xs">
                <p className="text-muted-foreground">
                  Minimum 6 characters, must contain numbers and special characters.
                </p>
                {passwordValidation && (
                  <div className="mt-1 space-y-1">
                    <p className={`font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                      Password Strength: {getPasswordStrengthText(passwordValidation.strength)}
                    </p>
                    {passwordValidation.errors.length > 0 && (
                      <ul className="text-red-500 space-y-0.5">
                        {passwordValidation.errors.map((validationError, index) => (
                          <li key={index}>• {validationError}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmNewPassword" className="block text-sm font-medium mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="confirmNewPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={userValues.confirmPassword || ""}
                  onChange={(e) =>
                    setUserValues({ ...userValues, confirmPassword: e.target.value })
                  }
                  className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                    userValues.confirmPassword &&
                    userValues.password !== userValues.confirmPassword
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder="Confirm new password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p
                className={`mt-1 text-xs ${
                  userValues.confirmPassword &&
                  userValues.password !== userValues.confirmPassword
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {userValues.confirmPassword &&
                userValues.password !== userValues.confirmPassword
                  ? "Passwords do not match"
                  : "Must match the new password above."}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>

            <div className="text-center text-sm text-muted-foreground">
              <button
                type="button"
                onClick={() => {
                  setShowResetCode(false);
                  setShowForgotPassword(false);
                  setResetCode("");
                  setError(null);
                  setSuccess(null);
                  setUserValues({});
                  setPasswordValidation(null);
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                }}
                className="text-primary hover:underline"
              >
                Back to sign in
              </button>
            </div>
          </form>
        ) : (
          /* Sign-in / Sign-up Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                 className="rounded-md bg-red-500/20 p-3 text-sm text-red-700 border border-red-500 flex items-center gap-2"
              >
                {error}
              </div>
            )}
            {success && (
              <div
                role="status"
                className="rounded-md bg-green-500/10 p-3 text-sm text-green-600 dark:text-green-400"
              >
                {success}
              </div>
            )}

            {isSignUp && (
              <>
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      id="firstName"
                      type="text"
                      value={userValues.firstName || ""}
                      onChange={(e) =>
                        setUserValues({ ...userValues, firstName: e.target.value })
                      }
                      className="w-full pl-10 pr-3 py-2 border rounded-md bg-background"
                      placeholder="Enter your first name"
                      required
                      autoComplete="given-name"
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                    Last Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      id="lastName"
                      type="text"
                      value={userValues.lastName || ""}
                      onChange={(e) =>
                        setUserValues({ ...userValues, lastName: e.target.value })
                      }
                      className="w-full pl-10 pr-3 py-2 border rounded-md bg-background"
                      placeholder="Enter your last name"
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={userValues.email || ""}
                  onChange={(e) =>
                    setUserValues({ ...userValues, email: e.target.value })
                  }
                  className="w-full pl-10 pr-3 py-2 border rounded-md bg-background"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={userValues.password || ""}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                    passwordValidation && !passwordValidation.isValid
                      ? "border-red-500"
                      : ""
                  }`}
                  placeholder={isSignUp ? "Create a password (min. 6 characters)" : "Enter your password"}
                  required
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {isSignUp && (
                <div className="mt-1 text-xs">
                  <p className="text-muted-foreground">
                    Minimum 6 characters, must contain numbers and special characters.
                  </p>
                  {passwordValidation && (
                    <div className="mt-1 space-y-1">
                      <p className={`font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                        Password Strength: {getPasswordStrengthText(passwordValidation.strength)}
                      </p>
                      {passwordValidation.errors.length > 0 && (
                        <ul className="text-red-500 space-y-0.5">
                          {passwordValidation.errors.map((validationError, index) => (
                            <li key={index}>• {validationError}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isSignUp && (
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setShowNewPassword(false);
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Confirm Password (sign up only) */}
            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={userValues.confirmPassword || ""}
                    onChange={(e) =>
                      setUserValues({ ...userValues, confirmPassword: e.target.value })
                    }
                    className={`w-full pl-10 pr-10 py-2 border rounded-md bg-background ${
                      userValues.confirmPassword &&
                      userValues.password !== userValues.confirmPassword
                        ? "border-red-500"
                        : ""
                    }`}
                    placeholder="Confirm your password"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p
                  className={`mt-1 text-xs ${
                    userValues.confirmPassword &&
                    userValues.password !== userValues.confirmPassword
                      ? "text-red-500"
                      : "text-muted-foreground"
                  }`}
                >
                  {userValues.confirmPassword &&
                  userValues.password !== userValues.confirmPassword
                    ? "Passwords do not match"
                    : "Must match the password above."}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading
                ? isSignUp
                  ? "Creating Account..."
                  : "Signing In..."
                : isSignUp
                ? "Create Account"
                : "Sign In"}
            </button>
          </form>
        )}

        {!showOTP && !showForgotPassword && !showNewPassword && !showResetCode && (
          <div className="text-center text-sm text-muted-foreground">
            {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setShowForgotPassword(false);
                setShowNewPassword(false);
                setError(null);
                setSuccess(null);
                setUserValues({});
                setShowPassword(false);
                setShowConfirmPassword(false);
                setPasswordValidation(null);
              }}
              className="text-primary hover:underline"
            >
              {isSignUp ? "Sign in" : "Sign up"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
